import Link from 'next/link'
import { CSS, keyframes } from 'style'

import frameworks from 'lib/constants/frameworks'

import Box from 'components/atomic/atoms/box'
import { IconStyled } from 'components/atomic/atoms/icon'
import Text from 'components/atomic/atoms/text'
import { NavbarDropdown } from 'components/pages/@layouts/layout-website/navbar-dropdown'

import { NavbarLinkProps } from './links'

const unfold = keyframes({
  '0%': { transform: 'rotateX(-40deg)' },
  '50%': { transform: 'rotateX(-10deg)' },
  '100%': { transform: 'rotateX(0deg)' },
})

const animation: CSS = {
  transformOrigin: 'var(--radix-popover-content-transform-origin)',
  animation: `${unfold} 0.25s ease-in forwards`,
}

type DropdownItemProps = Omit<NavbarLinkProps, 'href'> & {
  disabled?: boolean
}

const FrameworkLinks = () => {
  return (
    <Box
      flex
      items="center"
      justify="center"
      css={{ spaceX: 6, '@xl': { spaceX: 10 } }}
    >
      {frameworks.map(({ name, href, icon }, index) =>
        href ? (
          <Link key={`${name}-${index}`} href={href}>
            <IconStyled as={icon} size="4xl" aria-label={name} />
          </Link>
        ) : (
          <IconStyled
            key={`${name}-${index}`}
            as={icon}
            size="2xl"
            aria-label={name}
            css={{ opacity: 0.4, filter: 'grayscale(1)' }}
          />
        ),
      )}
    </Box>
  )
}

const DropdownItem = (props: DropdownItemProps) => {
  const { name, description, icon, disabled } = props
  return (
    <Box
      flex
      css={{
        size: '$full',
        spaceX: 4,
        py: '$4',
        px: '$5',
        border: '1px solid $transparent',
        borderRadius: '$md',
        ...(!disabled
          ? {
              '&:hover': { bgc: '$bg1', borderColor: '$bg3' },
            }
          : {
              opacity: 0.5,
              'user-select': 'none',
            }),
      }}
    >
      <Box>
        <Box flex justify="center" items="center" css={{ size: 38 }}>
          <IconStyled as={icon} size="2xl" color="text1" />
        </Box>
      </Box>
      <Box flex="column">
        <Text weight="semiBold">{name}</Text>
        <Text size="sm" color="text2">
          {description}
        </Text>
      </Box>
    </Box>
  )
}

type NavbarDropdownProps = {
  name: string
  items: NavbarLinkProps[]
}

export const NavbarDropdownResources = (props: NavbarDropdownProps) => {
  const { name, items } = props

  return (
    <NavbarDropdown name={name}>
      <Box
        css={{
          mt: '$6',
          '&:focus': { outline: 'none' },
          ...animation,
        }}
      >
        <Box
          flex="column"
          css={{
            container: 'sm',
            py: '$3',
            px: '$5',
            spaceY: 6,
            bgc: '$bg2',
            border: '1px solid $bg3',
            borderRadius: '$md',
            boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.2)',
            '.dark &': {
              boxShadow: 'none',
            },
            '@md': {
              container: 'md',
              py: '$6',
              px: '$10',
            },
            '@lg': {
              container: 'lg',
            },
          }}
        >
          <Box grid css={{ gap: '$3 $4', '@md': { gridCols: 2 } }}>
            {items.map(({ href, ...rest }, i) => {
              if (!href) {
                return (
                  <span key={i}>
                    <DropdownItem {...rest} disabled />
                  </span>
                )
              }
              const isExternalLink = href.startsWith('https://')
              if (isExternalLink) {
                return (
                  <a
                    key={href}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <DropdownItem {...rest} />
                  </a>
                )
              }
              return (
                <Link key={href} href={href}>
                  <DropdownItem {...rest} />
                </Link>
              )
            })}
          </Box>
          <FrameworkLinks />
        </Box>
      </Box>
    </NavbarDropdown>
  )
}
